/**
 * Notificações — regra pura (AD-002): interpolação, payload de leitura, cor e
 * título de cada aviso. Zero React, zero rede.
 *
 * O documento da caixa guarda `vars`, nunca HTML renderizado (AD-014). A peça é
 * montada na leitura: o servidor entrega o template por `$lookup` e a interpolação
 * acontece aqui.
 */

import {
  COMMUNICATION_SUBJECTS,
  CommunicationSubject,
  SUBJECT_LABEL,
} from 'pages/Preference/communication-preferences';

export type NotificationStatus = 'unread' | 'read';

/** As variáveis que o produtor resolveu por jogador: `target`, `qtd`/`saldo`, `data_limite`, `ano`. */
export type NotificationVars = Record<string, string | number>;

/**
 * Bloco declarativo do aviso: título, texto e destino.
 *
 * Vem de duas origens, e o servidor resolve a disputa com
 * `{"$ifNull": ["$inapp", <lookup do catálogo>]}` — o do documento ganha:
 *
 * - **Campanha** (CENTRAL-03.1): mora no catálogo e chega por `$lookup`. Copiá-lo
 *   por documento seria meio megabyte de string repetida (T-11).
 * - **Comunicado da DIPES** (DIPES-01.2): mora no próprio documento. Cada comunicado
 *   tem título próprio e não existe no catálogo, então o `$lookup` voltaria vazio e a
 *   linha cairia no rótulo genérico da família.
 */
export interface InappBlock {
  titulo: string;
  texto: string;
  cta_label?: string;
  cta_url?: string;
}

/** O documento como ele existe em `notification_inbox__c`. Nada além disto é gravado. */
export interface NotificationDoc {
  /** `<campanha>|<cycleRef>|<matrícula>` */
  _id: string;
  player: string;
  campaign: string;
  family: CommunicationSubject;
  cycleRef: string;
  templateId: string;
  vars: NotificationVars;
  status: NotificationStatus;
  created: number;
  expiresAt: number;
  readAt?: number;
  /** Só no comunicado da DIPES, que não tem catálogo de onde buscar (DIPES-01.2). */
  inapp?: InappBlock;
}

/** Linha da lista: o documento projetado + o `inapp` do catálogo. */
export interface NotificationRow {
  _id: string;
  campaign: string;
  family: CommunicationSubject;
  status: NotificationStatus;
  created: number;
  vars: NotificationVars;
  inapp?: InappBlock;
}

/** O aviso aberto: documento completo + o que veio dos dois `$lookup`. */
export interface NotificationDetail extends NotificationDoc {
  subject?: string;
  content?: string;
  /**
   * O servidor diz de onde veio o `inapp` que está aí em cima. Sem essa marca o
   * front não teria como distinguir os dois casos depois do `$ifNull`, e o PUT de
   * leitura — que é substituição — ou apagaria o bloco do comunicado, ou gravaria
   * o bloco do catálogo dentro de cada aviso de campanha.
   */
  inappProprio?: boolean;
}

/**
 * Casa `{{chave}}` e `{{{chave}}}`. As três chaves não são capricho: no Mustache
 * elas significam "não escape o HTML", e o template `dipes_comunicado` as usa no
 * `{{{corpo}}}` porque o corpo vem de um editor — com duas chaves, o e-mail
 * chegaria com as tags visíveis como texto.
 *
 * Aqui os dois casos são tratados igual, e isso é seguro porque a peça passa por
 * `sanitizeHtml` depois da interpolação, nunca antes.
 */
const PLACEHOLDER = /\{\{\{?(\w+)\}?\}\}/g;

/**
 * Substitui `{{chave}}` pelas variáveis do aviso.
 *
 * Não é motor Mustache e não precisa ser: medido nos 33 templates, todos usam
 * apenas variáveis simples — nenhuma seção, laço ou parcial.
 *
 * Variável sem fonte fica **literal**, de propósito. `{{pontos_bonificacao}}`
 * existe hoje em dois templates da Ecoa sem que nada o resolva; trocá-lo por
 * vazio esconderia o defeito, e por `undefined` produziria texto pior. Deixá-lo
 * visível é o que faz alguém corrigir o catálogo.
 */
export function interpolate(text: string, vars: NotificationVars): string {
  return text.replace(PLACEHOLDER, (literal, key) => {
    const value = vars[key];
    if (value === undefined || value === null) return literal;

    return String(value);
  });
}

/**
 * O payload de "marcar como lido". Gravar é PUT de **substituição**: campo
 * ausente é campo apagado, então todos os campos do documento voltam.
 *
 * Igualmente importante é o que NÃO volta. `subject` e `content` vêm de `$lookup`
 * e não pertencem ao documento — devolvê-los gravaria ~6 KB de HTML dentro de cada
 * aviso, que é exatamente o custo que AD-014 evitou ao guardar `vars` em vez da
 * peça pronta. Por isso a lista de campos é explícita aqui, e não um espalhamento
 * do objeto recebido.
 *
 * `inapp` é o caso que precisa dos dois comportamentos: no aviso de campanha ele
 * veio do catálogo e não pode ser gravado; no comunicado da DIPES ele É do
 * documento, e deixá-lo de fora apagaria o título do comunicado na primeira vez
 * que alguém o abrisse. Quem decide é `inappProprio`, que o servidor calcula
 * antes do `$ifNull`.
 */
export function toReadPayload(detail: NotificationDetail, now: number): NotificationDoc {
  return {
    ...(detail.inappProprio && detail.inapp ? { inapp: detail.inapp } : {}),
    _id: detail._id,
    player: detail.player,
    campaign: detail.campaign,
    family: detail.family,
    cycleRef: detail.cycleRef,
    templateId: detail.templateId,
    vars: detail.vars,
    status: 'read',
    created: detail.created,
    expiresAt: detail.expiresAt,
    readAt: now,
  };
}

/**
 * Cor de cada assunto, a mesma da faixa dos e-mails da nova identidade (NOTIF-10).
 * O comunicado da DIPES não tem e-mail no design e fica no ciano, o acento que
 * sobra na paleta. As classes ficam escritas por extenso para o Tailwind gerá-las.
 */
const FAMILY_ACCENT: Record<CommunicationSubject, string> = {
  reconhecimento: 'bg-brand-pink-light',
  realizacao_destaque: 'bg-brand-green',
  acoes_ecoa: 'bg-brand-purple-light',
  dipes: 'bg-brand-cyan-light',
};

function isKnownFamily(family: string): family is CommunicationSubject {
  return (COMMUNICATION_SUBJECTS as string[]).includes(family);
}

/** Classe de fundo do assunto. Assunto que o front ainda não conhece cai na cor neutra (NOTIF-17). */
export function familyAccent(family: string): string {
  if (!isKnownFamily(family)) return FAMILY_ACCENT.dipes;

  return FAMILY_ACCENT[family];
}

/** Nome do assunto para a pílula e para o título de reserva. */
export function familyLabel(family: string): string {
  if (!isKnownFamily(family)) return 'Aviso';

  return SUBJECT_LABEL[family];
}

/**
 * Título da linha. O `inapp` que chega aqui já é o resolvido pelo servidor:
 * o do próprio documento (comunicado da DIPES) > o `notificacao` do modelo de
 * e-mail > o da campanha, campo a campo (NOTIF-01).
 *
 * Sem título nenhum — campanha que teve o bloco removido depois de os avisos
 * existirem — cai no assunto da família, para a lista nunca mostrar uma linha
 * sem nome. O `$mergeObjects` devolve `{}` nesse caso, não a ausência do bloco.
 */
export function rowTitle(row: NotificationRow): string {
  if (!row.inapp?.titulo) return familyLabel(row.family);

  return interpolate(row.inapp.titulo, row.vars);
}

/** Frase curta do aviso, já interpolada; vazia quando o bloco não tem texto. */
export function rowText(row: NotificationRow): string {
  if (!row.inapp?.texto) return '';

  return interpolate(row.inapp.texto, row.vars);
}
